import React, { useCallback } from 'react';
import { TransactionStep, TransactionStepState, TxStepStatus } from '@/common/types';
import StyledButton from '@/components/StyledButton';

interface StepComponentProps {
  step: TransactionStep;
  isProcessing: boolean;
  retryAttempts: number;
  updateStepState: (updates: Partial<TransactionStepState>) => void;
  onExecute: () => void;
  onRestart: () => void;
}

export function StepComponent({
  step,
  isProcessing,
  retryAttempts,
  updateStepState,
  onExecute,
  onRestart,
}: StepComponentProps) {
  const getStatusIcon = useCallback(() => {
    switch (step.state.status) {
      case TxStepStatus.Confirmed:
        return (
          <div className="m-claim--size-2.5 m-claim--rounded-full m-claim--bg-surface-contrast" />
        );
      case TxStepStatus.Failed:
        return (
          <div className="m-claim--size-2.5 m-claim--rounded-full m-claim--bg-surface-destructive" />
        );
      case TxStepStatus.Confirming:
      case TxStepStatus.Pending:
        return (
          <div className="m-claim--size-1.5 m-claim--animate-ping m-claim--rounded-full m-claim--bg-surface-contrast" />
        );
      default:
        return <div className="m-claim--size-2.5 m-claim--rounded-full m-claim--bg-transparent" />;
    }
  }, [step]);

  const renderStepActions = useCallback(() => {
    // Confirming state
    if (step.state.status === TxStepStatus.Confirming) {
      return (
        <div className="m-claim--mt-2">
          <StyledButton
            version="primary"
            disabled={true}
          >
            Confirming...
          </StyledButton>
        </div>
      );
    }

    // Pending state - show execute button
    if (step.state.status === TxStepStatus.Pending || step.config.alwaysShow) {
      const SecondaryComponent = step.config.SecondaryComponent;
      return (
        <div className="m-claim--flex m-claim--flex-col m-claim--gap-2">
          {SecondaryComponent && (
            <div className="m-claim--relative m-claim--w-full">
              <SecondaryComponent updateStepState={updateStepState} />
            </div>
          )}

          {step.execute && (
            <StyledButton
              version="primary"
              disabled={isProcessing || !!step.state.error}
              onClick={onExecute}
              className="m-claim--mt-2"
            >
              {isProcessing ? 'Processing...' : step.config.primaryButtonText || 'Continue'}
            </StyledButton>
          )}
        </div>
      );
    }

    // Failed state - show retry/restart options
    if (step.state.status === TxStepStatus.Failed) {
      return (
        <div className="m-claim--mt-2 m-claim--flex m-claim--flex-row m-claim--gap-2">
          {retryAttempts < 3 ? (
            <>
              <StyledButton
                version="secondary"
                disabled={isProcessing}
                onClick={onRestart}
              >
                Restart
              </StyledButton>
              <StyledButton
                version="primary"
                disabled={isProcessing}
                onClick={onExecute}
              >
                Retry
              </StyledButton>
            </>
          ) : (
            <StyledButton
              version="secondary"
              onClick={() => window.open('https://support.manifold.xyz', '_blank')}
            >
              Contact Support
            </StyledButton>
          )}
        </div>
      );
    }

    // No actions for other states
    return null;
  }, [step, isProcessing, retryAttempts, onRestart, onExecute]);

  if (step.config.hideOnceCompleted && step.state.status === TxStepStatus.Confirmed) {
    return null;
  }

  return (
    <div className="m-claim--flex m-claim--flex-col m-claim--gap-4">
      {/* Status and step details */}
      <div className="m-claim--flex m-claim--items-center m-claim--gap-4">
        <div className="m-claim--relative m-claim--flex m-claim--size-6 m-claim--items-center m-claim--justify-center m-claim--rounded-full m-claim--border-2 m-claim--border-default m-claim--bg-surface-primary">
          {getStatusIcon()}
        </div>

        <div className="m-claim--flex m-claim--flex-1 m-claim--flex-col m-claim--items-start m-claim--justify-center">
          <div className="m-claim--flex m-claim--items-start m-claim--text-sm m-claim--font-semibold m-claim--text-primary">
            {step.config.name}
          </div>
          <div className="m-claim--flex m-claim--flex-col m-claim--gap-1">
            {step.state.descriptionByStatus[step.state.status] && (
              <div className="m-claim--text-sm m-claim--text-secondary">
                {step.state.descriptionByStatus[step.state.status]}
              </div>
            )}

            {step.state.error && (
              <div className="m-claim--text-xs m-claim--font-semibold m-claim--text-destructive">
                Error: {step.state.error}
              </div>
            )}

            {step.state.warning && (
              <div className="m-claim--text-xs m-claim--font-semibold m-claim--text-warning">
                Warning: {step.state.warning}
              </div>
            )}

            {step.state.etherscanLink &&
              (step.state.status === TxStepStatus.Confirmed ||
                step.state.status === TxStepStatus.Confirming) && (
                <div className="m-claim--text-sm m-claim--text-secondary">
                  <a
                    href={step.state.etherscanLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="m-claim--text-primary m-claim--underline hover:m-claim--text-secondary"
                  >
                    View transaction on Etherscan
                  </a>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      {renderStepActions()}
    </div>
  );
}
